import pdfIcon from "../assets/icons/pdf.svg";
import wordIcon from "../assets/icons/word.svg";
import defaultIcon from "../assets/icons/word.svg";
import excelIcon from "../assets/icons/microsoft-excel.svg";
import pptIcon from "../assets/icons/microsoft-powerpoint.svg";
import imageIcon from "../assets/icons/jpeg.svg";
import txtIcon from "../assets/icons/txt.svg";
import { getUserFriendlyErrorMessage } from "./validateFiles";

export const dataUrlToBlob = (dataUrl) => {
  const [metadata, base64Data] = dataUrl.split(",", 2);
  if (!metadata || !base64Data) throw new Error("Invalid image data");

  const mimeType = metadata.match(/^data:([^;]+);base64$/)?.[1] || "image/png";
  const binaryData = window.atob(base64Data);
  const bytes = new Uint8Array(binaryData.length);

  for (let index = 0; index < binaryData.length; index += 1) {
    bytes[index] = binaryData.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
};

export const getFileExtension = (fileOrPath) => {
  if (!fileOrPath) return "";
  const nameOrPath = typeof fileOrPath === 'string' ? fileOrPath : (fileOrPath?.name || fileOrPath?.filename || "");
  const parts = nameOrPath.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

export const getFileTypeCategory = (file) => {
  const ext = getFileExtension(file);

  if (["jpg", "jpeg", "png"].includes(ext)) return "image";
  if (["pdf"].includes(ext)) return "pdf";
  if (["doc", "docx"].includes(ext)) return "doc";
  if (["xls", "xlsx"].includes(ext)) return "excel";
  if (["ppt", "pptx"].includes(ext)) return "ppt";
  if (["txt"].includes(ext)) return "text";
  if (["json", "xml"].includes(ext)) return "json";
  if (["html"].includes(ext)) return "html";
  if (["csv"].includes(ext)) return "csv";
  if (["mp4", "webm", "ogg"].includes(ext)) return "video";

  return "other";
};

export const getFileNameFromUrl = (url) => {
  if (!url || typeof url !== "string") return "";

  try {
    const decodedUrl = decodeURIComponent(url);
    const fileName = decodedUrl.split("/").pop();
    if (!fileName) return "";
    const cleanedFileName = fileName.replace(/^[a-z0-9-]+_/, "");
    return cleanedFileName;
  } catch (error) {
    console.error("Error extracting file name from URL:", error);
    return "";
  }
};

export const getDocxUrlFromBase64 = (base64) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });

  return URL.createObjectURL(blob);
};

export const downloadImage = async (url, filename = "image.png") => {
  try {
    const response = await fetch(url, { mode: "cors" });
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Error downloading image:", error);
  }
};

export const getObjectUrlFromBase64 = (base64, type = "pdf") => {
  if (!base64) return null;

  try {
    const cleanedBase64 = base64
      .replace(/^data:[^;]+;base64,/, "")
      .replace(/\s/g, "");
    const binaryString = atob(cleanedBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    let mimeType;
    switch (type.toLowerCase()) {
      case "pdf":
        mimeType = "application/pdf";
        break;
      case "docx":
        mimeType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        break;
      case "xlsx":
        mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        break;
      case "jpg":
      case "jpeg":
        mimeType = "image/jpeg";
        break;
      case "png":
        mimeType = "image/png";
        break;
      case "txt":
        mimeType = "text/plain";
        break;
      default:
        mimeType = "application/octet-stream";
    }

    const blob = new Blob([bytes.buffer], { type: mimeType });
    return URL.createObjectURL(blob);
  } catch (e) {
    console.error("Error generating object URL:", e.message);
    return null;
  }
};

export const downloadFileFromBase64 = (
  base64Data,
  filename = "file",
  fileType = null
) => {
  if (!base64Data) {
    console.error("Base64 data is required");
    return false;
  }

  try {
    let cleanedBase64 = base64Data;
    if (cleanedBase64.includes(",")) {
      cleanedBase64 = cleanedBase64.split(",")[1];
    }
    cleanedBase64 = cleanedBase64.replace(/\s/g, "");

    let fileExtension = fileType;
    if (!fileExtension) {
      const extFromFilename = getFileExtension(filename);
      if (extFromFilename) {
        fileExtension = extFromFilename;
      } else {
        if (cleanedBase64.startsWith("/9j/")) {
          fileExtension = "jpg";
        } else if (cleanedBase64.startsWith("iVBORw0KGgo")) {
          fileExtension = "png";
        } else if (cleanedBase64.startsWith("JVBERi0")) {
          fileExtension = "pdf";
        } else {
          fileExtension = "pdf";
        }
      }
    }

    let mimeType;
    switch (fileExtension.toLowerCase()) {
      case "pdf":
        mimeType = "application/pdf";
        break;
      case "docx":
        mimeType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        break;
      case "doc":
        mimeType = "application/msword";
        break;
      case "xlsx":
        mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        break;
      case "xls":
        mimeType = "application/vnd.ms-excel";
        break;
      case "jpg":
      case "jpeg":
        mimeType = "image/jpeg";
        break;
      case "png":
        mimeType = "image/png";
        break;
      case "gif":
        mimeType = "image/gif";
        break;
      case "txt":
        mimeType = "text/plain";
        break;
      default:
        mimeType = "application/octet-stream";
    }

    const binaryString = atob(cleanedBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes.buffer], { type: mimeType });

    let finalFilename = filename;
    if (
      !finalFilename.toLowerCase().endsWith(`.${fileExtension.toLowerCase()}`)
    ) {
      finalFilename = `${finalFilename}.${fileExtension}`;
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error downloading file from base64:", error);
    return false;
  }
};

export const getFileExtensionBlobURL = (url) => {
  if (!url) return "";
  const pathname = new URL(url).pathname;
  const filename = pathname.substring(pathname.lastIndexOf("/") + 1);
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

export const getFileIcon = (file) => {
  const ext = getFileExtension(file);

  switch (ext) {
    case "pdf":
      return pdfIcon;
    case "doc":
    case "docx":
      return wordIcon;
    case "xls":
    case "xlsx":
      return excelIcon;
    case "ppt":
    case "pptx":
      return pptIcon;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return imageIcon;
    case "txt":
      return txtIcon;
    default:
      return defaultIcon;
  }
};

export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Convert a File/Blob to Base64 string (without prefix).
 * @param {File} file
 * @returns {Promise<string>}
 */
export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result;
      // remove "data:application/pdf;base64," prefix
      const base64String = result.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });

export const getFileExtensionFromPath = (path) => {
  if (!path) return null;
  return path.split(".").pop().toLowerCase();
};

// Get file name from path
export const getFileNameFromPath = (path) => {
  if (!path) return null;
  return path.substring(path.lastIndexOf("/") + 1);
};

export const downloadFile = async ({ url, filename }) => {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || `file-${Date.now()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
  } catch (error) {
    console.error("Download failed. Please try again.", error);
  }
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 100);
};

export const validateFileLimits = ({
  newFiles,
  selectedFilesLength,
  maxFileSize,
  maxAllowedFiles,
  constants
}) => {
  const oversizedFiles = newFiles.filter(file => file.size > maxFileSize);
  if (oversizedFiles.length > 0) {
    const sizeMB = maxFileSize / (1024 * 1024);
    const fileNames = oversizedFiles.map(f => f.name).join(", ");
    const errorMsg = (constants?.chat?.FILE_SIZE_LIMIT_EXCEEDED_MSG || "Each file must be under {size}MB. These are too large: {files}")
      .replace("{size}", sizeMB)
      .replace("{files}", fileNames);
    return { valid: false, errorMsg };
  }

  const totalFileCount = selectedFilesLength + newFiles.length;
  if (totalFileCount > maxAllowedFiles) {
    const errorMsg = (constants?.chat?.MAX_FILE_UPLOAD_LIMIT_MSG || "Cannot upload more than {count} file(s) in total.")
      .replace("{count}", maxAllowedFiles)
      .replace("file(s)", maxAllowedFiles > 1 ? "files" : "file");
    return { valid: false, errorMsg };
  }

  return { valid: true };
};

export const displayInvalidFileErrors = (invalidFiles, toast, constants = {}) => {
  if (!invalidFiles?.length) return;
  invalidFiles.forEach(invalidFile => {
    const match = invalidFile.match(/^(.+?)\s*\[(.+?)\]$/);
    if (match) {
      const [, filename, errorReason] = match;
      toast.error(`${filename}: ${getUserFriendlyErrorMessage(errorReason.trim(), constants)}`);
    } else {
      toast.error(`Invalid file: ${invalidFile}`);
    }
  });
};

